var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { GRID_DEFAULT_LOCALE_TEXT } from '@mui/x-data-grid';
import { enUS as datePickersEnUS } from '@mui/x-date-pickers/locales';
import i18n from 'i18next';
import { currencyCatalog } from '../components/common/currency/currencyCatalog';
import { RUM_EXTRA_ALLOWED_TEXT } from './privacyConfig';
var MAX_READABLE_LENGTH = 80;
var MAX_MEMO_ENTRIES = 5000;
// Digits plus punctuation and symbols that mean nothing on their own. No letters of any script.
var SAFE_CHARS = /^[\d\s+\-−–—.,:;/%()[\]|*#&=<>~'"!?•·…⚑▲▼↑↓°×]+$/;
var PHONE_CHARS = /^[+\d\s().\-–]+$/;
var PLAIN_DECIMAL = /^\(?[-−–]?\d+\.\d+\)?$/;
var FR_GROUPED = /^\(?[-+−–]?\d{1,3}(?:[   ]\d{3})+\)?$/;
var ISO_DATE_TOKEN = /^\d{4}-\d{2}(?:-\d{2})?$/;
var LEADING_PUNCTUATION = '([{\'"';
var TRAILING_PUNCTUATION = ')]}\'",;:.!?';
var ORDINAL = /^\d{1,4}(?:st|nd|rd|th|er|e)$/;
var PERIOD_TOKEN = /^(?:q[1-4]|w\d{1,2}|fy\d{2,4})$/;
// Unit glued to a number: 1.2k, 3.4m, 2x, 8h, 30s, 9:30am
var UNIT_SUFFIX = /(\d)(?:k|m|b|x|h|s|am|pm|a\.m\.|p\.m\.|secs?|mins?|hrs?)$/;
var MONEY_SHAPED = /\d[.,]\d/;
var MIN_PHONE_DIGITS = 9;
var MAX_PHONE_DIGITS = 15;
var MIN_PLUS_PHONE_DIGITS = 8;
var MIN_BARE_PHONE_DIGITS = 10;
// Plain scans rather than `x+$` regexes: these run on every text node Datadog records.
var trimTrailing = function (value, characters) {
    var end = value.length;
    while (end > 0 && characters.includes(value.charAt(end - 1)))
        end -= 1;
    return value.slice(0, end);
};
var stripWrappingPunctuation = function (token) {
    var start = 0;
    while (start < token.length && LEADING_PUNCTUATION.includes(token.charAt(start)))
        start += 1;
    return trimTrailing(token.slice(start), TRAILING_PUNCTUATION);
};
// No prototype, so arbitrary page text ("constructor", "toString") can never look like a known word.
var createDictionary = function () { return Object.create(null); };
var toWordSet = function (words) {
    var set = createDictionary();
    words.forEach(function (word) {
        set[word] = true;
    });
    return set;
};
var UNIT_WORDS = toWordSet(['am', 'pm', 'sec', 'secs', 'min', 'mins', 'hr', 'hrs', 'fte', 'hc']);
var calendarWords;
// Month and weekday names for the supported languages, lower-cased with trailing dots stripped ("janv.").
var getCalendarWords = function () {
    if (calendarWords)
        return calendarWords;
    var words = createDictionary();
    var add = function (date, options) {
        ['en', 'fr'].forEach(function (language) {
            var word = new Intl.DateTimeFormat(language, __assign(__assign({}, options), { timeZone: 'UTC' })).format(date);
            words[word.toLocaleLowerCase().replace(/\./g, '')] = true;
        });
    };
    for (var month = 0; month < 12; month += 1) {
        var date = new Date(Date.UTC(2021, month, 15));
        add(date, { month: 'long' });
        add(date, { month: 'short' });
    }
    // 2021-08-01 is a Sunday.
    for (var day = 1; day <= 7; day += 1) {
        var date = new Date(Date.UTC(2021, 7, day));
        add(date, { weekday: 'long' });
        add(date, { weekday: 'short' });
        add(date, { weekday: 'narrow' });
    }
    calendarWords = words;
    return words;
};
var currencyTokens;
var isCasedLetter = function (char) { return char.toLowerCase() !== char.toUpperCase(); };
// The currency symbols and codes come from our own static catalog, not from client data.
var getCurrencyTokens = function () {
    if (currencyTokens)
        return currencyTokens;
    var seen = createDictionary();
    var shortLetterOnly = createDictionary();
    currencyCatalog.forEach(function (_a) {
        var id = _a.id, name = _a.name;
        [id, name].forEach(function (token) {
            var value = token.trim().toLocaleLowerCase();
            if (!value)
                return;
            seen[value] = true;
            if (value.length < 3 && value.split('').every(isCasedLetter))
                shortLetterOnly[value] = true;
        });
    });
    currencyTokens = { all: Object.keys(seen).sort(function (a, b) { return b.length - a.length; }), shortLetterOnly: shortLetterOnly };
    return currencyTokens;
};
// Removes one currency symbol or code from the start or end of a number, e.g. "ca$1,234" -> "1,234".
var stripCurrency = function (value, fromEnd) {
    var _a = getCurrencyTokens(), all = _a.all, shortLetterOnly = _a.shortLetterOnly;
    for (var _i = 0, all_1 = all; _i < all_1.length; _i++) {
        var token = all_1[_i];
        var isAffix = fromEnd ? value.endsWith(token) : value.startsWith(token);
        if (isAffix && value.length > token.length) {
            var rest = fromEnd ? value.slice(0, value.length - token.length) : value.slice(token.length);
            if (!shortLetterOnly[token] || MONEY_SHAPED.test(rest))
                return rest;
        }
    }
    return value;
};
// A number with our own decorations around it: sign, currency, percent, unit.
var isDecoratedNumber = function (part) {
    var value = part.replace(/^[-+−–(]+/, '');
    value = stripCurrency(value, false).replace(/^[-−–]/, '');
    value = trimTrailing(value, ')%');
    value = trimTrailing(stripCurrency(value, true), '%');
    value = value.replace(UNIT_SUFFIX, '$1');
    return /\d/.test(value) && SAFE_CHARS.test(value);
};
var isCalendarWord = function (word) { return !!getCalendarWords()[word.replace(/\./g, '')]; };
var isStaticWord = function (word) {
    return isCalendarWord(word) ||
        !!UNIT_WORDS[word] ||
        PERIOD_TOKEN.test(word) ||
        ORDINAL.test(word) ||
        getCurrencyTokens().all.includes(word);
};
var isReadablePart = function (part) {
    return !part || SAFE_CHARS.test(part) || isStaticWord(part) || isDecoratedNumber(part);
};
var isReadableToken = function (token) {
    if (SAFE_CHARS.test(token))
        return true;
    var core = stripWrappingPunctuation(token);
    if (isReadablePart(core))
        return true;
    // Compounds such as "26-jan-05" or "09:30am-10:00am"
    var parts = core.split(/[-–—/]/);
    return parts.length > 1 && parts.every(isReadablePart);
};
/**
 * Phone numbers must never be sent, so phone-shaped digit strings are the one kind of number that stays
 * masked: a "+" with 8+ digits, a bare run of 10+ digits, or 9-15 digits in groups that are not a number.
 */
var looksLikePhone = function (text) {
    if (!PHONE_CHARS.test(text) || PLAIN_DECIMAL.test(text) || FR_GROUPED.test(text))
        return false;
    var digits = text.replace(/\D/g, '').length;
    if (/^\(?\+/.test(text))
        return digits >= MIN_PLUS_PHONE_DIGITS;
    var groups = text.split(/[\s().\-–]+/).filter(Boolean);
    if (groups.length === 1)
        return digits >= MIN_BARE_PHONE_DIGITS;
    if (text.split(/\s+/).every(function (token) { return ISO_DATE_TOKEN.test(token) || /^[-–]$/.test(token); }))
        return false;
    return digits >= MIN_PHONE_DIGITS && digits <= MAX_PHONE_DIGITS;
};
var computeReadableValue = function (text) {
    if (!text || text.length > MAX_READABLE_LENGTH || looksLikePhone(text))
        return false;
    if (SAFE_CHARS.test(text))
        return true;
    var tokens = text.split(/\s+/);
    if (/\d/.test(text))
        return tokens.every(isReadableToken);
    // No digits: only calendar text such as "January" or "Mon - Fri"
    return tokens.every(function (token) { return SAFE_CHARS.test(token) || isCalendarWord(stripWrappingPunctuation(token)); });
};
var memo = createDictionary();
var memoSize = 0;
/**
 * True for value-only text that identifies nobody: numbers, amounts, percentages, times and dates, with
 * the currency symbols, units and month/day names we render around them. Anything containing other
 * letters is not readable. Expects trimmed, lower-cased input.
 */
export var isReadableValue = function (text) {
    var known = memo[text];
    if (known !== undefined)
        return known;
    var isReadable = computeReadableValue(text);
    if (memoSize >= MAX_MEMO_ENTRIES) {
        memo = createDictionary();
        memoSize = 0;
    }
    memo[text] = isReadable;
    memoSize += 1;
    return isReadable;
};
// Bounded, and no braces inside, so a run of "{" cannot make the scan quadratic.
var PLACEHOLDER = /\{\{[^{}]{0,80}\}\}/;
var MIN_TEMPLATE_STATIC_LENGTH = 3;
var isPlainText = function (value) { return !value.includes('$t(') && !value.includes('<'); };
// Locale messages with placeholders, stored as their static parts: "showing {{from}}-{{to}} of {{total}}"
// -> ["showing ", "-", " of ", ""].
var templates = [];
var templateKeys = createDictionary();
var addTemplate = function (text) {
    var key = text.toLocaleLowerCase();
    if (templateKeys[key])
        return;
    templateKeys[key] = true;
    var parts = key.split(PLACEHOLDER);
    var hasAdjacentPlaceholders = parts.slice(1, -1).some(function (part) { return !part; });
    var staticLength = parts.join('').replace(/[\s\d!-/:-@[-`{-~]/g, '').length;
    if (!hasAdjacentPlaceholders && staticLength >= MIN_TEMPLATE_STATIC_LENGTH)
        templates.push(parts);
};
// True when the text is one of the messages above with every placeholder filled by something readable.
var matchesTemplate = function (text, isFilled) {
    return templates.some(function (parts) {
        if (!text.startsWith(parts[0]))
            return false;
        var last = parts.length - 1;
        var position = parts[0].length;
        for (var index = 1; index <= last; index += 1) {
            var part = parts[index];
            var found = index === last ? text.length - part.length : text.indexOf(part, position + 1);
            if (found < position || !text.startsWith(part, found))
                return false;
            var gap = text.slice(position, found).trim();
            if (!gap || !isFilled(gap))
                return false;
            position = found + part.length;
        }
        return true;
    });
};
/**
 * Adds every static string leaf of a locale bundle (or any nested part of the resource store). Leaves with
 * `{{placeholders}}` are kept as templates instead, see matchesTemplate.
 */
export var collectStaticStrings = function (node, into) {
    if (typeof node === 'string') {
        var text = node.trim();
        if (!text || !isPlainText(text))
            return;
        if (PLACEHOLDER.test(text))
            addTemplate(text);
        else
            into.add(text.toLocaleLowerCase());
        return;
    }
    if (node && typeof node === 'object') {
        Object.keys(node).forEach(function (key) { return collectStaticStrings(node[key], into); });
    }
};
// Splits "America/New York (14:35)" into its label and its bracketed value.
var splitBracketedSuffix = function (value) {
    var open = value.lastIndexOf('(');
    if (open <= 0 || !value.endsWith(')'))
        return undefined;
    var label = value.slice(0, open).trim();
    var inner = value.slice(open + 1, -1);
    return label && inner && !inner.includes(')') ? [label, inner] : undefined;
};
var allowlist;
// Staging set for registerRumStaticText, so labels registered before install are not lost.
var registeredTexts = new Set();
/**
 * Registers static labels that are hard-coded in our own source (metric names, time-zone labels), so they
 * stay readable like locale strings. Call it at module level, next to the constant. Never pass data that
 * came from the API or from a user.
 */
export var registerRumStaticText = function (texts) {
    collectStaticStrings(texts, registeredTexts);
    if (allowlist)
        collectStaticStrings(texts, allowlist);
};
var createAllowlist = function () {
    var set = new Set();
    var nativeHas = Set.prototype.has;
    var isSimplyReadable = function (value) { return nativeHas.call(set, value) || isReadableValue(value); };
    // Also "<label> (<value>)", e.g. "America/New York (14:35)", when both halves are readable on their own.
    var isReadable = function (value) {
        if (isSimplyReadable(value))
            return true;
        var bracketed = splitBracketedSuffix(value);
        return !!bracketed && isSimplyReadable(bracketed[0]) && isSimplyReadable(bracketed[1]);
    };
    // Patched on the instance: common-ui compiles to ES5, where `class extends Set` throws at runtime.
    set.has = function (text) {
        try {
            var value = String(text).trim();
            return isReadable(value) || matchesTemplate(value, isReadable);
        }
        catch (_a) {
            // Datadog calls this for every text node it records and must never throw; text we cannot judge stays masked.
            return false;
        }
    };
    return set;
};
var watchLocaleStore = function (set) {
    var store = i18n.store;
    collectStaticStrings(store.data, set);
    // Fires before the namespace's first render, so the text is allowlisted by the time it is recorded.
    store.on('added', function (language, namespace) { var _a, _b; return collectStaticStrings((_b = (_a = store.data) === null || _a === void 0 ? void 0 : _a[language]) === null || _b === void 0 ? void 0 : _b[namespace], set); });
};
/** Creates the allowlist once, fills it from the loaded locale bundles and exposes it to the SDK. */
export var installRumAllowlist = function () {
    if (!allowlist) {
        var set_1 = createAllowlist();
        collectStaticStrings(RUM_EXTRA_ALLOWED_TEXT, set_1);
        // Built-in texts of the libraries we render: grid menus and footers, date picker buttons.
        collectStaticStrings(GRID_DEFAULT_LOCALE_TEXT, set_1);
        collectStaticStrings(datePickersEnUS, set_1);
        registeredTexts.forEach(function (text) { return set_1.add(text); });
        if (i18n.store) {
            watchLocaleStore(set_1);
        }
        else {
            i18n.on('initialized', function () { return watchLocaleStore(set_1); });
        }
        allowlist = set_1;
    }
    window.$DD_ALLOW = allowlist;
    return allowlist;
};
/** True when Datadog would record this text unmasked: our own static text or a value-only string. */
export var isRumReadableText = function (text) {
    return typeof text === 'string' && !!allowlist && allowlist.has(text.toLocaleLowerCase());
};
/** @internal Reset for tests only */
export var resetRumAllowlistForTests = function () {
    allowlist = undefined;
    templates = [];
    templateKeys = createDictionary();
    delete window.$DD_ALLOW;
};
